// Suppress harmless ResizeObserver errors (Vuetify/browser issue)
const _RO = window.ResizeObserver
window.ResizeObserver = class extends _RO {
  constructor(cb: ResizeObserverCallback) {
    super((entries: ResizeObserverEntry[], observer: ResizeObserver) => {
      requestAnimationFrame(() => { cb(entries, observer) })
    })
  }
}
window.addEventListener('error', (e: ErrorEvent) => {
  if (e.message?.includes('ResizeObserver')) {
    e.stopImmediatePropagation()
    e.preventDefault()
  }
})

// Only import Sentry in non-development environments to prevent HMR issues
let Sentry: any = null;
if (process.env['VUE_APP_ENVIRONMENT'] !== 'development') {
  Sentry = require('@sentry/vue');
}

// Disable Sentry completely in development to prevent replay warnings
if (process.env['VUE_APP_ENVIRONMENT'] === 'development') {
  // Set environment variable to disable Sentry
  (process as any).env.SENTRY_DISABLE = 'true';
  // Disable any global Sentry instance
  if (typeof window !== 'undefined' && (window as any).Sentry) {
    (window as any).Sentry.close();
  }
}

import 'flag-icons/css/flag-icons.min.css';
import 'v-phone-input/dist/v-phone-input.css';
import { createVPhoneInput } from 'v-phone-input';
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import InfiniteLoading from '@/components/ui/InfiniteLoading.vue';
import UiTypography from '@/components/ui/Typography/Typography.vue';
import VueCookies from 'vue-cookies';
import moment from 'moment-timezone';
import { MaskInput } from 'vue-3-mask';
import CryptoJS from 'crypto-js';

import App from './App.vue';
import vuetify from './plugins/vuetify';
import router from '@modules/app/router';
import store from './store';
import { CONFIG } from '@/const/config';
import ErrorPlugin from '@/plugins/error/error';
import { applyFontStyles } from './assets/fonts/fonts';
import './scss/main.scss';
import i18n from '@/plugins/i18n';

// Create Vue 3 app instance
const app = createApp(App);

// Apply custom font styles
applyFontStyles();

// Create Pinia instance
const pinia = createPinia();

// Global kw_event function for chat widget (if not defined)
declare global {
  interface Window {
    kw_event?: (eventName: string, data: any) => void;
    kw?: (command: string, params: any) => void;
  }
}

// Initialize global chat widget functions
const initializeChatWidget = (): void => {
  if (typeof window !== 'undefined' && !window.kw_event) {
    window.kw_event = function (eventName: string, data: any) {
      console.log('kw_event called:', eventName, data);
    };
  }
  if (typeof window !== 'undefined' && !window.kw) {
    window.kw = function (command: string, params: any) {
      console.log('kw called:', command, params);
    };
  }
};

// Custom v-mask directive for Vue 3
const createMaskDirective = () => ({
  mounted(el: HTMLElement, binding: any) {
    const input = el.querySelector('input') || el;
    if (!input) return;
    const mask = binding.value;
    if (!mask) return;

    const applyMask = (value: string): string => {
      if (!value) return '';
      let result = '';
      let valueIndex = 0;
      for (let i = 0; i < mask.length && valueIndex < value.length; i++) {
        const maskChar = mask[i];
        const valueChar = value[valueIndex];
        if (maskChar === '#') {
          if (valueChar && /\d/.test(valueChar)) {
            result += valueChar;
            valueIndex++;
          }
        } else {
          result += maskChar;
          if (valueChar === maskChar) {
            valueIndex++;
          }
        }
      }
      return result;
    };

    const handleInput = (event: any) => {
      if (event._isProgrammatic) {
        return;
      }
      const value = event.target.value.replace(/\D/g, '');
      const maskedValue = applyMask(value);
      if (event.target.value !== maskedValue) {
        event.target.value = maskedValue;
        const inputEvent = new Event('input', { bubbles: true });
        (inputEvent as any)._isProgrammatic = true;
        event.target.dispatchEvent(inputEvent);
      }
    };

    input.addEventListener('input', handleInput);
    (el as any)._maskHandler = handleInput;
  },
  unmounted(el: HTMLElement) {
    const input = el.querySelector('input') || el;
    if (input && (el as any)._maskHandler) {
      input.removeEventListener('input', (el as any)._maskHandler);
    }
  },
});

// Global money directive
const createMoneyDirective = () => ({
  mounted(el: HTMLElement) {
    const input = el.getElementsByTagName('input')[0];
    if (!(input instanceof HTMLInputElement)) {
      throw new TypeError("Can't apply v-money directive to a non-input element");
    }

    const decimalsFormat = () => {
      let { value } = input;
      const originalLen = value.length;
      let caretPos = input.selectionStart || 0;
      if (value) {
        value = value
          .replace(/\./, ',')
          .replace(/[^\d,]/g, '')
          .replace(/^0+(?!,|$)/, '')
          .replace(/,/, 'x')
          .replace(/,/g, '')
          .replace(/x/, ',');
        let decimalPos = value.indexOf(',');
        if (decimalPos < 0) {
          decimalPos = value.length;
        } else if (decimalPos === 0 && value.length > 1) {
          value = `0${value}`;
        }
        const decimals = value.substring(0, decimalPos).split(' ').join('');
        input.value =
          decimals.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + value.substr(decimalPos, 3);
        const updatedLen = input.value.length;
        caretPos = updatedLen - originalLen + caretPos;
        input.setSelectionRange(caretPos, caretPos);
        input.dispatchEvent(new Event('input'));
      }
    };

    const coinsFormat = () => {
      const { value } = input;
      if (value && value !== ',') {
        const decimalPos = value.indexOf(',');
        if (decimalPos >= 0) {
          let coinsInput = value.substring(decimalPos);
          coinsInput += '00';
          input.value = input.value.substring(0, decimalPos) + coinsInput.substr(0, 3);
        } else {
          input.value = `${input.value},00`;
        }
      } else {
        input.value = '';
      }
      input.dispatchEvent(new Event('input'));
    };

    input.addEventListener('blur', coinsFormat);
    el.addEventListener('input', decimalsFormat);
  },
});

// Global filters
const createGlobalFilters = () => ({
  amountWithDashSeparator: (value: string | number): string => {
    if (!value) return '0,00';
    const parts = value.toString().split('.');
    let integerPart = parts[0] || '';
    const decimalPart = parts[1] || '00';
    
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return decimalPart ? `${integerPart},${decimalPart}` : integerPart;
  },
  moment: (value: any, format: string): string => {
    if (!value) return '';
    return moment(value).format(format);
  },
});

// Initialize Zoho Desk ASAP Sidebar
const initializeZohoAsap = (): void => {
  try {
    if (!CONFIG.VUE_APP_ZOHO_ASAP_ENABLED) return;
    if (typeof window === 'undefined' || !document) return;

    const scriptUrl = CONFIG.VUE_APP_ZOHO_ASAP_SCRIPT_URL;
    const portalId = CONFIG.VUE_APP_ZOHO_PORTAL_ID;
    const appId = CONFIG.VUE_APP_ZOHO_APP_ID;
    if (!scriptUrl || !portalId || !appId) return;

    // Avoid duplicate injection (support both our id and Zoho's default ids)
    if (
      document.getElementById('zoho-asap-script') ||
      document.getElementById('zohodeskasapscript') ||
      document.getElementById('zohodeskasap')
    ) {
      return;
    }

    // Optional: Ready queue shim for compatibility with Zoho snippet
    if (!(window as any).ZohoDeskAsapReady) {
      (window as any).ZohoDeskAsapReady = function (cb: any) {
        const q = ((window as any).ZohoDeskAsap__asyncalls = (window as any).ZohoDeskAsap__asyncalls || []);
        if ((window as any).ZohoDeskAsapReadyStatus) {
          cb && q.push(cb);
          q.forEach((fn: any) => fn && fn());
          (window as any).ZohoDeskAsap__asyncalls = null;
        } else {
          cb && q.push(cb);
        }
      };
    }

    const s = document.createElement('script');
    // Align with Zoho's default id to prevent duplicates
    s.id = 'zohodeskasapscript';
    s.type = 'text/javascript';
    s.defer = true;
    s.src = scriptUrl;
    // Support CSP nonce if provided
    const nonce: any = (CONFIG as any).VUE_APP_ZOHO_NONCE;
    if (nonce) (s as any).nonce = nonce;
    const x = document.getElementsByTagName('script')[0];
    x.parentNode?.insertBefore(s, x);
  } catch (e) {
    // Fail-safe: never block app init
    console.warn('Zoho ASAP initialization failed:', e);
  }
};

// Initialize Sentry
const initializeSentry = (): void => {
  if (process.env['VUE_APP_ENVIRONMENT'] !== 'development' && Sentry) {
    Sentry.init({
      app,
      dsn: CONFIG.VUE_APP_SENTRY_DSN,
      logErrors: true,
      attachProps: true,
      attachStacktrace: true,
      integrations: [
        new Sentry.BrowserTracing({
          routingInstrumentation: Sentry.vueRouterInstrumentation(router),
          tracingOrigins: ['localhost', 'b2b.dev.dzing.com', /^\//],
        }),
        new Sentry.Breadcrumbs({
          console: true,
          fetch: true,
          sentry: true,
          xhr: true,
        }),
        new Sentry.HttpContext(),
        new Sentry.FunctionToString(),
      ],
      tracingOptions: { trackComponents: true },
      tracesSampleRate: process.env['VUE_APP_ENVIRONMENT'] === 'production' ? 0.3 : 1,
      environment: process.env['VUE_APP_ENVIRONMENT'],
      release: `${process.env['VUE_APP_NAME']}@${process.env['VUE_APP_VERSION']}`,
      debug: process.env['VUE_APP_ENVIRONMENT'] !== 'production',
      enabled: true,
    });
  }
};

// Initialize v-phone-input
const initializeVPhoneInput = () => {
  const vPhoneInput = createVPhoneInput({
    countryIconMode: 'svg',
    displayFormat: 'international',
    enableSearchingCountry: true,
    persistentPlaceholder: true,
    persistentHint: true,
    defaultCountry: 'GB',
    preferredCountries: ['GB', 'US', 'DE', 'FR', 'IT', 'ES', 'PL'],
    mode: 'international',
  });

  return vPhoneInput;
};

// Initialize the application
const initializeApp = async (): Promise<void> => {
  // Initialize global functions
  initializeChatWidget();

  // Register global components
  app.component('MaskInput', MaskInput);
  app.component('infinite-loading', InfiniteLoading);
  app.component('ui-typography', UiTypography);

  // Register directives
  app.directive('mask', createMaskDirective());
  app.directive('money', createMoneyDirective());

  // Set global properties
  (app.config.globalProperties as any)['$filters'] = createGlobalFilters();
  (app.config.globalProperties as any)['$moment'] = moment;
  (app.config.globalProperties as any)['$cookies'] = VueCookies as any;
  (app.config.globalProperties as any)['$CryptoJS'] = CryptoJS;

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const wb: any = require('./registerServiceWorker').default;
  (app.config.globalProperties as any)['$workbox'] = wb;
  (app.config.globalProperties as any)['$appStore'] = store;

  // Initialize plugins
  app.use(VueCookies as any);
  app.use(ErrorPlugin);
  app.use(initializeVPhoneInput());

  // Initialize monitoring services
  initializeSentry();
  initializeZohoAsap();

  // Mount the app
  app.use(router).use(store).use(pinia).use(i18n).use(vuetify).mount('#app');
};

// Start the application
initializeApp().catch((error) => {
  console.error('Failed to initialize application:', error);
});
